import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';

export type TipoBot = 'RPA' | 'ETL' | 'WEBSCRAP';

export interface IBotConfig {
  bot_name: string;
  tipo_bot: TipoBot;
  apelido: string | null;
  descricao: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface IBotComConfig {
  bot_name: string;
  tipo_bot: TipoBot;
  apelido: string | null;
  descricao: string | null;
  tem_config: boolean;
}

interface IBotConfigResponse {
  status: string;
  message: string;
  data: IBotConfig | null;
}

interface IBotConfigListResponse {
  status: string;
  message: string;
  data: IBotConfig[];
}

interface IBotsComConfigResponse {
  status: string;
  message: string;
  data: IBotComConfig[];
}

interface IBotConfigUpdateResponse {
  status: string;
  message: string;
  data: {
    config: IBotConfig;
    logs_atualizados: number | null;
  };
}

export interface IBotConfigUpdateDTO {
  apelido?: string | null;
  descricao?: string | null;
}

export interface IBotConfigFilters {
  tipo_bot?: TipoBot;
}

export const useBotConfig = () => {
  /**
   * Lista todos os bots que existem nos logs, junto com suas configurações
   */
  const getTodosBots = async (key: QueryFunctionContext): Promise<IBotComConfig[]> => {
    const filters = (key.queryKey?.[1] as IBotConfigFilters) ?? {};
    const res = await api.get<IBotsComConfigResponse>('/api/v1/orquestrador/bots/todos', {
      params: filters,
    });
    return res.data.data;
  };

  /**
   * Lista todas as configurações cadastradas
   */
  const getConfigs = async (key: QueryFunctionContext): Promise<IBotConfig[]> => {
    const filters = (key.queryKey?.[1] as IBotConfigFilters) ?? {};
    const res = await api.get<IBotConfigListResponse>('/api/v1/orquestrador/bots/config', {
      params: filters,
    });
    return res.data.data;
  };

  /**
   * Obtém a configuração de um bot específico
   */
  const getConfig = async (tipoBot: TipoBot, botName: string): Promise<IBotConfig | null> => {
    const res = await api.get<IBotConfigResponse>(
      `/api/v1/orquestrador/bots/config/${tipoBot}/${encodeURIComponent(botName)}`,
    );
    return res.data.data;
  };

  /**
   * Atualiza a configuração de um bot e propaga para os logs existentes
   */
  const updateConfig = async (
    tipoBot: TipoBot,
    botName: string,
    data: IBotConfigUpdateDTO,
    propagarLogs = true,
  ): Promise<{ config: IBotConfig; logs_atualizados: number | null }> => {
    const res = await api.patch<IBotConfigUpdateResponse>(
      `/api/v1/orquestrador/bots/config/${tipoBot}/${encodeURIComponent(botName)}`,
      data,
      {
        params: { propagar_logs: propagarLogs },
      },
    );
    return res.data.data;
  };

  /**
   * Cria ou atualiza a configuração de um bot
   */
  const createOrUpdateConfig = async (data: {
    bot_name: string;
    tipo_bot: TipoBot;
    apelido?: string | null;
    descricao?: string | null;
  }): Promise<IBotConfig> => {
    const res = await api.post<IBotConfigResponse>('/api/v1/orquestrador/bots/config', data);
    return res.data.data as IBotConfig;
  };

  /**
   * Propaga a configuração para todos os logs existentes do bot
   */
  const propagarConfig = async (
    tipoBot: TipoBot,
    botName: string,
  ): Promise<{ bot_name: string; tipo_bot: TipoBot; logs_atualizados: number }> => {
    const res = await api.post<{
      status: string;
      message: string;
      data: { bot_name: string; tipo_bot: TipoBot; logs_atualizados: number };
    }>(`/api/v1/orquestrador/bots/config/${tipoBot}/${encodeURIComponent(botName)}/propagar`);
    return res.data.data;
  };

  /**
   * Deleta a configuração de um bot
   */
  const deleteConfig = async (tipoBot: TipoBot, botName: string): Promise<void> => {
    await api.delete(`/api/v1/orquestrador/bots/config/${tipoBot}/${encodeURIComponent(botName)}`);
  };

  return {
    getTodosBots,
    getConfigs,
    getConfig,
    updateConfig,
    createOrUpdateConfig,
    propagarConfig,
    deleteConfig,
  };
};
